/**
 * useSimpleTableFields
 *
 * Computes the visible and searchable field lists for SimpleTable.
 * Depends on column order, visible column set, and role-based field filtering.
 */

import { useMemo } from "react";
import type { SimpleTableConfig, SimpleFieldConfig, BaseEntity } from "../types";

interface UseSimpleTableFieldsProps<TData extends BaseEntity> {
  config: SimpleTableConfig<TData>;
  visibleColumns: Set<string>;
  columnOrder: string[];
  filterFieldsByRole: (
    fields: SimpleTableConfig<TData>["fields"]
  ) => SimpleTableConfig<TData>["fields"];
}

export function useSimpleTableFields<TData extends BaseEntity>({
  config,
  visibleColumns,
  columnOrder,
  filterFieldsByRole,
}: UseSimpleTableFieldsProps<TData>) {
  /**
   * Visible fields in column order, filtered by role and visibility toggle.
   */
  const visibleFields = useMemo(() => {
    const roleFilteredFields = filterFieldsByRole(config.fields);

    return columnOrder
      .filter((fieldId) => visibleColumns.has(fieldId) && roleFilteredFields[fieldId])
      .map((fieldId) => [fieldId, roleFilteredFields[fieldId]] as [string, SimpleFieldConfig]);
  }, [config.fields, visibleColumns, columnOrder, filterFieldsByRole]);

  /**
   * Field IDs that participate in client-side search.
   * Fields with searchable === false are excluded.
   */
  const searchableFields = useMemo(() => {
    const roleFilteredFields = filterFieldsByRole(config.fields);

    return Object.entries(roleFilteredFields)
      .filter(([, fieldConfig]) => fieldConfig.searchable !== false)
      .map(([fieldId]) => fieldId);
  }, [config.fields, filterFieldsByRole]);

  return { visibleFields, searchableFields };
}
